import type { LegalDocument } from "../legal/types"

/**
 * Bilingual (English / Arabic) content for the Privacy Policy page.
 * Governing law: State of Qatar. Contact and legal-entity details are left as
 * clearly-marked placeholders to be finalised by legal counsel.
 */
export const privacyContent: LegalDocument = {
  title: {
    en: "Privacy Policy",
    ar: "سياسة الخصوصية",
  },
  lastUpdated: {
    en: "3 September 2026",
    ar: "٣ سبتمبر ٢٠٢٦",
  },
  intro: {
    en: "This Privacy Policy explains how the Taqat AI Employee Store (the “Service”) collects, uses, shares, and protects your personal data when you use our platform and related services. By using the Service, you agree to the practices described here.",
    ar: "توضّح سياسة الخصوصية هذه كيفية قيام متجر “طاقة” للموظفين المدعومين بالذكاء الاصطناعي (“الخدمة”) بجمع بياناتك الشخصية واستخدامها ومشاركتها وحمايتها عند استخدامك لمنصّتنا وخدماتنا المرتبطة بها. وباستخدامك للخدمة، فإنك توافق على الممارسات الموضَّحة هنا.",
  },
  sections: [
    {
      id: "scope",
      heading: { en: "Scope", ar: "النطاق" },
      body: [
        {
          type: "p",
          en: "This Policy applies to personal data we process about visitors, account holders, and end users of the Service. It does not apply to third-party services that have their own privacy policies.",
          ar: "تنطبق هذه السياسة على البيانات الشخصية التي نعالجها بشأن الزوّار وأصحاب الحسابات والمستخدمين النهائيين للخدمة. ولا تنطبق على خدمات الأطراف الثالثة التي لها سياسات خصوصية خاصة بها.",
        },
      ],
    },
    {
      id: "information-we-collect",
      heading: { en: "Information We Collect", ar: "المعلومات التي نجمعها" },
      body: [
        {
          type: "list",
          en: [
            "Account information: name, email address, organisation, and credentials you provide when registering.",
            "Content you provide: prompts, messages, files, knowledge-base data, and agent configurations.",
            "Usage data: interactions with the Service, feature usage, device and browser information, and log data.",
            "Integration data: information exchanged with third-party tools you connect to your workspace.",
            "Payment information: billing details processed by our payment providers (we do not store full card numbers).",
          ],
          ar: [
            "معلومات الحساب: الاسم والبريد الإلكتروني والمؤسسة وبيانات الاعتماد التي تقدّمها عند التسجيل.",
            "المحتوى الذي تقدّمه: الأوامر النصية والرسائل والملفات وبيانات قاعدة المعرفة وإعدادات الوكلاء.",
            "بيانات الاستخدام: التفاعلات مع الخدمة واستخدام الميزات ومعلومات الجهاز والمتصفّح وبيانات السجلّ.",
            "بيانات التكامل: المعلومات المتبادَلة مع أدوات الأطراف الثالثة التي تربطها بمساحة عملك.",
            "معلومات الدفع: تفاصيل الفوترة التي يعالجها مزوّدو خدمات الدفع لدينا (نحن لا نخزّن أرقام البطاقات الكاملة).",
          ],
        },
      ],
    },
    {
      id: "how-we-use",
      heading: { en: "How We Use Your Information", ar: "كيفية استخدامنا لمعلوماتك" },
      body: [
        {
          type: "list",
          en: [
            "To provide, operate, and maintain the Service.",
            "To process your requests and generate AI Employee responses.",
            "To improve, secure, and troubleshoot the Service.",
            "To communicate with you about your account, updates, and support.",
            "To comply with legal obligations and enforce our terms.",
          ],
          ar: [
            "لتقديم الخدمة وتشغيلها وصيانتها.",
            "لمعالجة طلباتك وتوليد ردود الموظفين الأذكياء.",
            "لتحسين الخدمة وتأمينها وحلّ مشكلاتها.",
            "للتواصل معك بشأن حسابك والتحديثات والدعم.",
            "للامتثال للالتزامات القانونية وإنفاذ شروطنا.",
          ],
        },
      ],
    },
    {
      id: "ai-processing",
      heading: { en: "AI Processing & Sub-Processors", ar: "معالجة الذكاء الاصطناعي والمعالجون من الباطن" },
      body: [
        {
          type: "p",
          en: "To generate responses, the content you submit may be transmitted to third-party AI model providers acting as our sub-processors. These providers process your content solely to return a result and are bound by their own commitments regarding data handling. We take reasonable steps to work with providers that maintain appropriate safeguards.",
          ar: "لتوليد الردود، قد يُنقَل المحتوى الذي تقدّمه إلى مزوّدي نماذج الذكاء الاصطناعي من الأطراف الثالثة الذين يعملون كمعالجين من الباطن لدينا. ويعالج هؤلاء المزوّدون محتواك لغرض إرجاع النتيجة فقط، وهم ملتزمون بتعهّداتهم الخاصة بشأن التعامل مع البيانات. ونتّخذ خطوات معقولة للعمل مع مزوّدين يحافظون على ضمانات مناسبة.",
        },
      ],
    },
    {
      id: "how-we-share",
      heading: { en: "How We Share Information", ar: "كيفية مشاركتنا للمعلومات" },
      body: [
        {
          type: "p",
          en: "We do not sell your personal data. We may share it with:",
          ar: "نحن لا نبيع بياناتك الشخصية. وقد نشاركها مع الجهات التالية:",
        },
        {
          type: "list",
          en: [
            "Service providers and sub-processors who help us operate the Service (such as hosting and AI providers).",
            "Third-party integrations you choose to connect.",
            "Authorities or parties where required by law, legal process, or to protect rights and safety.",
            "A successor entity in connection with a merger, acquisition, or sale of assets.",
          ],
          ar: [
            "مزوّدو الخدمات والمعالجون من الباطن الذين يساعدوننا في تشغيل الخدمة (مثل مزوّدي الاستضافة والذكاء الاصطناعي).",
            "تكاملات الأطراف الثالثة التي تختار ربطها.",
            "الجهات المختصة أو الأطراف عند طلب القانون أو الإجراءات القانونية أو لحماية الحقوق والسلامة.",
            "جهة خَلَف في سياق عملية اندماج أو استحواذ أو بيع أصول.",
          ],
        },
      ],
    },
    {
      id: "retention",
      heading: { en: "Data Retention", ar: "الاحتفاظ بالبيانات" },
      body: [
        {
          type: "p",
          en: "We retain personal data for as long as needed to provide the Service, comply with our legal obligations, resolve disputes, and enforce our agreements. When data is no longer required, we delete or anonymise it.",
          ar: "نحتفظ بالبيانات الشخصية طالما دعت الحاجة إلى ذلك لتقديم الخدمة والامتثال لالتزاماتنا القانونية وحلّ النزاعات وإنفاذ اتفاقياتنا. وعندما لا تعود البيانات مطلوبة، نقوم بحذفها أو جعلها مجهولة الهوية.",
        },
      ],
    },
    {
      id: "security",
      heading: { en: "Data Security", ar: "أمن البيانات" },
      body: [
        {
          type: "p",
          en: "We implement technical and organisational measures designed to protect your data against unauthorised access, loss, or misuse, including encryption in transit and access controls. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
          ar: "نطبّق تدابير تقنية وتنظيمية مصمَّمة لحماية بياناتك من الوصول غير المصرّح به أو الفقدان أو سوء الاستخدام، بما في ذلك التشفير أثناء النقل وضوابط الوصول. ولا توجد وسيلة نقل أو تخزين آمنة تماماً، ولا يمكننا ضمان الأمان المطلق.",
        },
      ],
    },
    {
      id: "international-transfers",
      heading: { en: "International Data Transfers", ar: "نقل البيانات دولياً" },
      body: [
        {
          type: "p",
          en: "Your data may be processed in countries other than your own, including where our service providers operate. Where required, we take appropriate steps to ensure such transfers are subject to adequate safeguards.",
          ar: "قد تتم معالجة بياناتك في دول غير دولتك، بما في ذلك حيث يعمل مزوّدو الخدمات لدينا. وحيثما يُطلب ذلك، نتّخذ الخطوات المناسبة لضمان خضوع عمليات النقل هذه لضمانات كافية.",
        },
      ],
    },
    {
      id: "your-rights",
      heading: { en: "Your Rights", ar: "حقوقك" },
      body: [
        {
          type: "p",
          en: "Subject to applicable law, you may have the right to access, correct, delete, or export your personal data, and to object to or restrict certain processing. To exercise these rights, contact us using the details below.",
          ar: "وفقاً للقانون المعمول به، قد يكون لك الحق في الوصول إلى بياناتك الشخصية أو تصحيحها أو حذفها أو تصديرها، وفي الاعتراض على معالجة معيّنة أو تقييدها. ولممارسة هذه الحقوق، يُرجى التواصل معنا باستخدام التفاصيل الواردة أدناه.",
        },
      ],
    },
    {
      id: "cookies",
      heading: { en: "Cookies & Tracking", ar: "ملفات تعريف الارتباط والتتبّع" },
      body: [
        {
          type: "p",
          en: "We use cookies and similar technologies to keep you signed in, remember preferences, and analyse usage. You can control cookies through your browser settings; disabling some cookies may affect the functionality of the Service.",
          ar: "نستخدم ملفات تعريف الارتباط والتقنيات المشابهة لإبقائك مسجّل الدخول وتذكّر تفضيلاتك وتحليل الاستخدام. ويمكنك التحكّم في ملفات تعريف الارتباط من خلال إعدادات متصفّحك؛ وقد يؤثّر تعطيل بعضها في وظائف الخدمة.",
        },
      ],
    },
    {
      id: "childrens-privacy",
      heading: { en: "Children's Privacy", ar: "خصوصية الأطفال" },
      body: [
        {
          type: "p",
          en: "The Service is not directed to children under 18, and we do not knowingly collect personal data from them. If you believe a child has provided us with personal data, please contact us so we can take appropriate action.",
          ar: "الخدمة غير موجَّهة للأطفال دون سنّ 18 عاماً، ونحن لا نجمع عمداً بيانات شخصية منهم. وإذا كنت تعتقد أن طفلاً قد زوّدنا ببيانات شخصية، فيُرجى التواصل معنا لكي نتّخذ الإجراء المناسب.",
        },
      ],
    },
    {
      id: "changes",
      heading: { en: "Changes to this Policy", ar: "التغييرات على هذه السياسة" },
      body: [
        {
          type: "p",
          en: "We may update this Privacy Policy from time to time. When we make material changes, we will provide notice by reasonable means and update the “Last updated” date above.",
          ar: "قد نُحدّث سياسة الخصوصية هذه من وقت لآخر. وعندما نُجري تغييرات جوهرية، سنقدّم إشعاراً بالوسائل المعقولة ونُحدّث تاريخ “آخر تحديث” أعلاه.",
        },
      ],
    },
    {
      id: "contact",
      heading: { en: "Contact Us", ar: "تواصل معنا" },
      body: [
        {
          type: "p",
          en: "For privacy questions or to exercise your rights, contact us at info@taqat.qa.",
          ar: "لأي استفسارات تتعلق بالخصوصية أو لممارسة حقوقك، يُرجى التواصل معنا عبر info@taqat.qa.",
        },
      ],
    },
  ],
}
